import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NaturezaMovimentacaoEnum } from '../../../../../shared/enums/financas/natureza-movimentacao.enum';
import { PeriodoBuscaEnum } from '../../../../../shared/enums/dfe/periodo-busca.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { ConciliacaoCartaoCreditoService } from '../../../../../shared/services/apis/financas/conciliacao-cartao-credito.service';
import { CustomDateAdapter } from '../../../../../shared/services/custom-date-adapter';
import { LimitDateValidator } from '../../../../../shared/validators/limit-date.validator';
import { ModoConciliacaoCartaoEnum } from '../../../../../shared/enums/financas/modo-conciliacao-cartao.enum';
import {
    BuscarParaAssociarCartaoResponse,
    MovimentacaoCartaoCandidata,
    ParcelaCartaoCandidata,
} from '../../../../../shared/services/responses/financas/buscar-para-associar-cartao.response';
import { AjusteParcelaCartao, ConciliarCartaoCreditoRequest } from '../../../../../shared/services/requests/financas/conciliar-cartao.request';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';

export interface ConciliarCartaoDialogData {
    idExtratoBancario: number;
    idNaturezaMovimentacao: number;
    dataTransacao: string;
    valor: number;
    memo: string | null;
}

@Component({
    selector: 'app-conciliar-cartao-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatMenuModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AbntPipe,
        AlertCardComponent,
    ],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
    ],
    templateUrl: './conciliar-cartao-dialog.component.html',
    styleUrls: ['./conciliar-cartao-dialog.component.css'],
})
export class ConciliarCartaoDialogComponent implements OnInit, OnDestroy {
    public readonly Math = Math;
    public naturezaEnum = NaturezaMovimentacaoEnum;
    public periodoBuscaEnum = PeriodoBuscaEnum;
    public isSaida = false;

    // ── Tela ───────────────────────────────────────────────────────────
    public tela: 'associar' | 'revisar' = 'associar';

    // ── Período ────────────────────────────────────────────────────────
    public periodoSelecionado: PeriodoBuscaEnum = PeriodoBuscaEnum.Ultimos30Dias;
    public limiteMaxDias = 60;
    private periodoSelecionadoAplicado: PeriodoBuscaEnum = PeriodoBuscaEnum.Ultimos30Dias;
    private periodoIntervaloDeAplicado: Date | null = null;
    private periodoIntervaloAteAplicado: Date | null = null;
    private periodoMenuFoiAplicado = false;

    @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;

    // ── Filtros / form ─────────────────────────────────────────────────
    public model!: FormGroup;
    public tiposDisponiveis: { label: string; value: string | null }[] = [];

    // ── Associar ───────────────────────────────────────────────────────
    public isCarregandoCandidatos = false;
    public isLoadingMais = false;
    public isConciliando = false;
    public parcelasCandidatas: ParcelaCartaoCandidata[] = [];
    public movsCandidatas: MovimentacaoCartaoCandidata[] = [];
    public selecionadosCp = new Set<number>();
    public selecionadosMov = new Set<number>();
    public _paginaAtual = 0;
    public _temMais = false;

    // ── Revisar valores ────────────────────────────────────────────────
    public formRevisar!: FormGroup;
    public formArray!: FormArray;
    public currencyMask: any;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ConciliarCartaoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConciliarCartaoDialogData,
        private cartaoService: ConciliacaoCartaoCreditoService,
        private maskService: MaskService,
        private alertService: AlertService,
    ) {
        this.isSaida = data.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Saida;
        this.currencyMask = this.maskService.getCurrencyMask();
    }

    ngOnInit(): void {
        // Tipos disponíveis
        this.tiposDisponiveis = [
            { label: 'Todos', value: null },
            { label: 'Movimentação', value: 'Movimentacao' },
        ];
        if (this.isSaida) {
            this.tiposDisponiveis.push({ label: 'Contas a Pagar', value: 'ContaPagar' });
        }

        // Datas padrão: últimos 30 dias
        const hoje = new Date();
        const trintaDiasAtras = new Date();
        trintaDiasAtras.setDate(hoje.getDate() - 30);

        this.model = this.fb.group({
            dataDe: [trintaDiasAtras, Validators.required],
            dataAte: [hoje, Validators.required],
            tipo: [null],
            busca: [''],
        }, { validators: LimitDateValidator.rangeLimit(2) });

        // Revisar form (CPs use formArray built on demand; movimentações use per-item formArray)
        this.formRevisar = this.fb.group({});

        this.salvarPeriodoAplicado();
        this.carregarCandidatos(true);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ── Período label ──────────────────────────────────────────────────

    get periodoLabel(): string {
        switch (this.periodoSelecionado) {
            case PeriodoBuscaEnum.Hoje: return 'hoje';
            case PeriodoBuscaEnum.DaSemana: return 'desta semana';
            case PeriodoBuscaEnum.DoMes: return 'do mês';
            case PeriodoBuscaEnum.Ultimos30Dias: return 'últimos 30 dias';
            case PeriodoBuscaEnum.DoIntervalo: return 'do intervalo';
            default: return 'últimos 30 dias';
        }
    }

    // ── Período actions ────────────────────────────────────────────────

    public selecionarPeriodo(periodo: PeriodoBuscaEnum): void {
        this.periodoSelecionado = periodo;
        if (periodo !== PeriodoBuscaEnum.DoIntervalo) {
            this.calcularDatasParaPeriodo(periodo);
        }
    }

    private calcularDatasParaPeriodo(periodo: PeriodoBuscaEnum): void {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        let dataDe: Date;
        let dataAte: Date;

        switch (periodo) {
            case PeriodoBuscaEnum.Hoje:
                dataDe = new Date(hoje);
                dataAte = new Date(hoje);
                break;
            case PeriodoBuscaEnum.DaSemana: {
                dataDe = new Date(hoje);
                dataDe.setDate(hoje.getDate() - hoje.getDay());
                dataAte = new Date(dataDe);
                dataAte.setDate(dataDe.getDate() + 6);
                break;
            }
            case PeriodoBuscaEnum.DoMes: {
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                dataAte = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
                break;
            }
            case PeriodoBuscaEnum.Ultimos30Dias:
            default: {
                dataDe = new Date(hoje);
                dataDe.setDate(hoje.getDate() - 30);
                dataAte = new Date(hoje);
                break;
            }
        }

        this.model.patchValue({ dataDe, dataAte });
    }

    public aplicarPeriodo(trigger?: MatMenuTrigger): void {
        if (this.periodoSelecionado === PeriodoBuscaEnum.DoIntervalo) {
            this.model.get('dataDe')?.markAsTouched();
            this.model.get('dataAte')?.markAsTouched();

            if (this.model.get('dataAte')?.hasError('dataInvalida')) {
                this.alertService.show('Atenção', 'A data final não pode ser menor que a data inicial.', AlertType.warning);
                return;
            }
            if (this.model.get('dataAte')?.hasError('intervaloExcedido')) {
                this.alertService.show('Atenção', `O intervalo máximo permitido é de ${this.limiteMaxDias} dias.`, AlertType.warning);
                return;
            }
            if (this.model.get('dataDe')?.hasError('required') || this.model.get('dataAte')?.hasError('required')) {
                this.alertService.show('Atenção', 'Preencha as duas datas do intervalo.', AlertType.warning);
                return;
            }

            const de = new Date(this.model.get('dataDe')!.value);
            const ate = new Date(this.model.get('dataAte')!.value);
            const diffDias = Math.ceil((ate.getTime() - de.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDias > this.limiteMaxDias) {
                this.alertService.show('Atenção', `O intervalo máximo permitido é de ${this.limiteMaxDias} dias.`, AlertType.warning);
                return;
            }
        }

        this.periodoMenuFoiAplicado = true;
        this.salvarPeriodoAplicado();
        this.carregarCandidatos(true);
        const menuTrigger = trigger ?? this.periodoMenuTrigger;
        setTimeout(() => menuTrigger?.closeMenu());
    }

    public prepararPeriodoIntervalo(): void {
        this.periodoMenuFoiAplicado = false;
    }

    public cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
        this.restaurarPeriodoAplicado();
        this.periodoMenuFoiAplicado = true;
        const menuTrigger = trigger ?? this.periodoMenuTrigger;
        setTimeout(() => menuTrigger?.closeMenu());
    }

    public onPeriodoMenuClosed(): void {
        if (!this.periodoMenuFoiAplicado) {
            this.restaurarPeriodoAplicado();
        }
    }

    private salvarPeriodoAplicado(): void {
        this.periodoSelecionadoAplicado = this.periodoSelecionado;
        this.periodoIntervaloDeAplicado = this.cloneDate(this.model?.get('dataDe')?.value ?? null);
        this.periodoIntervaloAteAplicado = this.cloneDate(this.model?.get('dataAte')?.value ?? null);
    }

    private restaurarPeriodoAplicado(): void {
        this.periodoSelecionado = this.periodoSelecionadoAplicado;
        this.model.patchValue({
            dataDe: this.cloneDate(this.periodoIntervaloDeAplicado),
            dataAte: this.cloneDate(this.periodoIntervaloAteAplicado),
        });
    }

    private cloneDate(data: Date | null): Date | null {
        return data ? new Date(data) : null;
    }

    // ── Busca ──────────────────────────────────────────────────────────

    public carregarCandidatos(resetar = true): void {
        if (resetar) {
            if (this.isCarregandoCandidatos) return;
            this._paginaAtual = 0;
            this.parcelasCandidatas = [];
            this.movsCandidatas = [];
            this.selecionadosCp.clear();
            this.selecionadosMov.clear();
            this._temMais = false;
            this.isCarregandoCandidatos = true;
        } else {
            if (this.isLoadingMais) return;
            this.isLoadingMais = true;
        }

        const v = this.model.getRawValue();
        const dataInicio = v.dataDe ? this._formatDate(new Date(v.dataDe)) : null;
        const dataFim = v.dataAte ? this._formatDate(new Date(v.dataAte)) : null;

        this.cartaoService.buscarParaAssociar({
            idExtratoBancario: this.data.idExtratoBancario,
            termoBusca: v.busca || null,
            dataInicio,
            dataFim,
        }).pipe(takeUntil(this._destroy$))
        .subscribe({
            next: (res: BuscarParaAssociarCartaoResponse) => {
                this.isCarregandoCandidatos = false;
                this.isLoadingMais = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao buscar candidatos.', AlertType.error);
                    return;
                }
                if (resetar) {
                    this.parcelasCandidatas = res.parcelasContasPagar || [];
                    this.movsCandidatas = res.movimentacoes || [];
                } else {
                    this.parcelasCandidatas = [...this.parcelasCandidatas, ...(res.parcelasContasPagar || [])];
                    this.movsCandidatas = [...this.movsCandidatas, ...(res.movimentacoes || [])];
                }
                // A API atual não tem paginação, mas preparamos para suporte futuro
                this._temMais = false;
            },
            error: () => {
                this.isCarregandoCandidatos = false;
                this.isLoadingMais = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarCandidatos(false);
    }

    // ── Seleção multi-select (CPs e movimentações) ────────────────────

    public toggleCp(id: number): void {
        if (this.selecionadosCp.has(id)) this.selecionadosCp.delete(id);
        else this.selecionadosCp.add(id);
    }

    public isCpSelecionado(id: number): boolean { return this.selecionadosCp.has(id); }

    public toggleMov(id: number): void {
        if (this.selecionadosMov.has(id)) this.selecionadosMov.delete(id);
        else this.selecionadosMov.add(id);
    }

    public isMovSelecionada(id: number): boolean { return this.selecionadosMov.has(id); }

    public limparSelecao(): void {
        this.selecionadosCp.clear();
        this.selecionadosMov.clear();
    }

    get temSelecao(): boolean {
        return this.isSaida ? this.selecionadosCp.size > 0 : this.selecionadosMov.size > 0;
    }

    // ── Getters de seleção ─────────────────────────────────────────────

    get parcelasSelecionadas(): ParcelaCartaoCandidata[] {
        return this.parcelasCandidatas.filter(p => this.selecionadosCp.has(p.idContasPagarParcela));
    }

    get movimentacoesSelecionadas(): MovimentacaoCartaoCandidata[] {
        return this.movsCandidatas.filter(m => this.selecionadosMov.has(m.idMovimentacao));
    }

    get movimentacoesSelecionadasParaRevisar(): MovimentacaoCartaoCandidata[] {
        return this.movimentacoesSelecionadas;
    }

    get totalSelecionadoCp(): number {
        let total = 0;
        for (const id of this.selecionadosCp) {
            const p = this.parcelasCandidatas.find(x => x.idContasPagarParcela === id);
            if (p) total += p.valor;
        }
        return +total.toFixed(2);
    }

    get valorSelecionado(): number {
        if (this.isSaida) return this.totalSelecionadoCp;
        return +this.movimentacoesSelecionadas.reduce((sum, m) => sum + m.valor, 0).toFixed(2);
    }

    get valorExtrato(): number {
        return Math.abs(this.data.valor);
    }

    get diferenca(): number {
        return +(this.valorExtrato - this.valorSelecionado).toFixed(2);
    }

    get podeConciliar(): boolean {
        return this.temSelecao && Math.abs(this.diferenca) < 0.01;
    }

    get podeRevisar(): boolean {
        return this.temSelecao && Math.abs(this.diferenca) >= 0.01;
    }

    // ── Getters de revisar ─────────────────────────────────────────────

    public formAt(i: number): FormGroup { return this.formArray.at(i) as FormGroup; }

    public valorFinalItem(i: number): number {
        if (this.isSaida) {
            const p = this.parcelasSelecionadas[i];
            const v = this.formAt(i).value;
            return +(p.valor + (v.valorJurosPrevisto || 0) + (v.valorMultaPrevisto || 0) + (v.valorAcrescimoPrevisto || 0) - (v.valorDescontoPrevisto || 0)).toFixed(2);
        } else {
            return +(this.formAt(i).get('novoValor')?.value || 0).toFixed(2);
        }
    }

    get valorComAjuste(): number {
        const count = this.isSaida ? this.parcelasSelecionadas.length : this.movimentacoesSelecionadas.length;
        return +Array.from({ length: count }, (_, i) => this.valorFinalItem(i)).reduce((a, b) => a + b, 0).toFixed(2);
    }

    get diferencaRevisar(): number {
        return +(this.valorExtrato - this.valorComAjuste).toFixed(2);
    }

    get podeConciliarRevisar(): boolean {
        return Math.abs(this.diferencaRevisar) < 0.01;
    }

    // ── Navegação telas ────────────────────────────────────────────────

    public onIrParaRevisar(): void {
        if (!this.podeRevisar) return;
        if (this.isSaida) {
            // Build FormArray — one group per selected parcela
            const parcelas = this.parcelasSelecionadas;
            this.formArray = this.fb.array(
                parcelas.map(() => this.fb.group({
                    valorJurosPrevisto: [0],
                    valorMultaPrevisto: [0],
                    valorDescontoPrevisto: [0],
                    valorAcrescimoPrevisto: [0],
                }))
            );
        } else {
            // Build FormArray — one group per selected movimentação
            const movs = this.movimentacoesSelecionadas;
            this.formArray = this.fb.array(
                movs.map(m => this.fb.group({
                    novoValor: [m.valor],
                }))
            );
        }
        this.tela = 'revisar';
    }

    public voltarParaAssociar(): void {
        this.tela = 'associar';
    }

    // ── Confirmar direto (sem diferença) ──────────────────────────────

    public confirmarDireto(): void {
        if (!this.podeConciliar || this.isConciliando) return;
        this.executarAssociar(null, null, null, null);
    }

    // ── Confirmar revisar ──────────────────────────────────────────────

    public confirmarRevisar(): void {
        if (!this.podeConciliarRevisar || this.isConciliando) return;
        if (this.isSaida) {
            const ajustes: AjusteParcelaCartao[] = this.parcelasSelecionadas.map((_, i) => {
                const v = this.formAt(i).value;
                return {
                    valorJurosPrevisto: v.valorJurosPrevisto || 0,
                    valorMultaPrevisto: v.valorMultaPrevisto || 0,
                    valorDescontoPrevisto: v.valorDescontoPrevisto || 0,
                    valorAcrescimoPrevisto: v.valorAcrescimoPrevisto || 0,
                };
            });
            this.executarAssociar(null, null, ajustes, null);
        } else {
            const novosValores = this.movimentacoesSelecionadas.map((m, i) => {
                const novoValor = +(this.formAt(i).get('novoValor')?.value || m.valor);
                return Math.abs(novoValor - m.valor) > 0.001 ? novoValor : null;
            });
            this.executarAssociar(null, null, null, novosValores);
        }
    }

    // ── Executar conciliação ───────────────────────────────────────────

    private executarAssociar(
        _unused1: null,
        _unused2: null,
        ajustes: AjusteParcelaCartao[] | null,
        novosValores: (number | null)[] | null
    ): void {
        this.isConciliando = true;

        let request: ConciliarCartaoCreditoRequest;
        if (this.isSaida) {
            request = {
                idExtratoBancario: this.data.idExtratoBancario,
                modo: ModoConciliacaoCartaoEnum.AssociarCp,
                idContasPagarParcelas: Array.from(this.selecionadosCp),
                ajustes: ajustes ?? undefined,
            };
        } else {
            request = {
                idExtratoBancario: this.data.idExtratoBancario,
                modo: ModoConciliacaoCartaoEnum.AssociarMovimentacao,
                idMovimentacoes: Array.from(this.selecionadosMov),
                novosValores: novosValores ?? undefined,
            };
        }

        this.cartaoService.conciliar(request).pipe(takeUntil(this._destroy$)).subscribe({
            next: res => {
                this.isConciliando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao conciliar.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Conciliação realizada.', AlertType.success);
                this.dialogRef.close({ conciliado: true });
            },
            error: () => {
                this.isConciliando = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public cancelar(): void {
        this.dialogRef.close({ conciliado: false });
    }

    // ── Utilitários ────────────────────────────────────────────────────

    public onDateInput(event: Event): void {
        MaskService.onDateInput(event);
    }

    private _formatDate(date: Date): string {
        return date.toISOString().split('T')[0];
    }
}
